import { ButtonV2, Flex, TextV2, useToast } from '@withjoy/joykit';
import React, { useState } from 'react';
import { LayoutVirtualScrollableArea } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutVirtualScrollableArea';
import { pxToRem } from '@withjoy/joykit/theme';
import { PhotoUploadProps } from '../../Photo.types';
import { useTranslation } from '@shared/core';
import { Import, PhotoUpload as PhotoUploadIcon, Trash, WarningTriangle } from '@withjoy/joykit/icons';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import {
  ComponentType,
  useAddPhotoComponentMutation,
  useUpdatePhotoComponentMutation,
  useUploadPhotoFromUrlMutation,
  WebsiteEditorPhotoComponentFragment,
} from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { CUSTOM_PAGE_ACTIONS } from '@website-editor/customComponents/CustomComponents.constants';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useFilestack } from '@shared/utils/filestack';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { CheckIcon } from '@assets/icons';
import { useWebsiteEditorCustomPageTelemetry } from '@website-editor/features/custom-page/WebsiteEditorCustomPage.telemetry';
import { useWebsiteEditorPhotoTelemetry } from '../../Photo.telemetry';
import { BottomActions } from '@website-editor/features/custom-page/components/BottomActions/BottomActions';

export const PhotoUpload: React.FC<PhotoUploadProps> = props => {
  const { containerId, pageSlug, photoData, isEditMode, isSingleComponent } = props;

  const [isLoadingPhoto, setIsLoadingPhoto] = useState(false);
  const [imageUrl, setImageUrl] = useState(photoData?.photo?.url);
  const [uploadedImageData, setUploadedImageData] = useState(photoData?.photo?.id || '');

  const { tq } = useTranslation('websiteEditor');

  const { replaceAndNavigateToScreen } = useNavigationPaneContext();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { setCustomComponentBlockData, setShowDeletePopup, setDeleteComponentDetails } = useCustomComponentStateContext<WebsiteEditorPhotoComponentFragment>();
  const { toast } = useToast();
  const customPageTelemetry = useWebsiteEditorCustomPageTelemetry();
  const photoTelemetry = useWebsiteEditorPhotoTelemetry();

  const [uploadPhotoFromUrl, { loading: waitingUploadPhoto }] = useUploadPhotoFromUrlMutation();
  const [addPhotoComponent, { loading: addingPhotoComponent }] = useAddPhotoComponentMutation({
    onCompleted: result => {
      customPageTelemetry.websitePageComponentUpdated(pageSlug, 'Add', {
        action: 'added',
        id: result.addPhotoComponent.id,
        componentType: ComponentType.photo,
      });
      toast(tq`Success! Photo has been saved.`, { icon: <CheckIcon /> });
      replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
    },
    onError: () => {
      toast(tq`You have reached the block limit. To add more blocks please create a new page or replace an existing block.`, { icon: <WarningTriangle />, duration: 5000 });
    },
  });
  const [updatePhotoComponent, { loading: updatingPhotoComponent }] = useUpdatePhotoComponentMutation({
    onCompleted: result => {
      customPageTelemetry.websitePageComponentUpdated(pageSlug, 'Update', {
        action: 'updated',
        id: result.updatePhotoComponent.id,
        componentType: ComponentType.photo,
      });
      toast(tq`Success! Photo has been saved.`, { icon: <CheckIcon /> });
      setCustomComponentBlockData({} as WebsiteEditorPhotoComponentFragment);
      replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
    },
    onError: () => {
      toast(tq`An error occurred while saving your photo. Please try again.`, { icon: <WarningTriangle /> });
    },
  });

  const isLoading = isLoadingPhoto || waitingUploadPhoto || addingPhotoComponent || updatingPhotoComponent;

  const { open: openFilePicker, getPhotoPreviewUrl } = useFilestack({
    containerId,
    accept: 'image/*',
    onUploadDone: async (res): Promise<void> => {
      setIsLoadingPhoto(true);
      if (res.filesFailed.length) {
        toast(tq`An error occurred while saving your photo. Please try again.`, { icon: <WarningTriangle /> });
        openFilePicker();
        return;
      }

      const { filename, url } = res.filesUploaded[0];
      const previewUrl = await getPhotoPreviewUrl({ url });
      setImageUrl(previewUrl);
      setIsLoadingPhoto(false);

      try {
        const { data } = await uploadPhotoFromUrl({
          variables: {
            payload: {
              assetId: filename,
              containerId,
              url: previewUrl,
            },
          },
        });
        const customPhotoId = data?.uploadPhotoFromUrl?.id;
        if (customPhotoId) {
          setUploadedImageData(customPhotoId);
        } else {
          toast(tq`An error occurred while saving your photo. Please try again.`, { icon: <WarningTriangle /> });
        }
      } catch {
        toast(tq`An error occurred while saving your photo. Please try again.`, { icon: <WarningTriangle /> });
      }
    },
    onClose: () => {
      setIsLoadingPhoto(false);
    },
    maxFiles: 1,
  });

  const handleAddPhoto = useEventCallback(() => {
    photoTelemetry.uploadPhotoClicked(pageSlug, 'add');
    openFilePicker();
  });

  const handleReplacePhoto = useEventCallback(() => {
    photoTelemetry.uploadPhotoClicked(pageSlug, 'replace');
    openFilePicker();
  });

  const handleDeleteBlock = useEventCallback(() => {
    if (photoData?.id) {
      setShowDeletePopup(true);
      setDeleteComponentDetails({
        id: photoData.id,
        type: photoData.__typename,
        title: tq`Are you sure you want to delete this photo block?`,
        subTitle: tq`The contents of this section will be lost. This can’t be undone.`,
        deleteCta: tq`Delete Photo Block`,
        cancelCta: tq`Cancel`,
        successMessage: tq`Success! Photo has been deleted.`,
        errorMessage: tq`An error occurred while deleting your photo. Please try again.`,
      });
    }
  });

  const handleCancel = useEventCallback(() => {
    setCustomComponentBlockData({} as WebsiteEditorPhotoComponentFragment);
    replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
  });

  const handleSave = useEventCallback(() => {
    if (isEditMode && photoData?.id) {
      updatePhotoComponent({
        variables: {
          payload: {
            id: photoData.id,
            photoId: uploadedImageData,
            sortOrder: photoData.sortOrder,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: ['PageContainerBySlug'],
      }).then(() => {
        updateWebsitePreview();
      });
    } else {
      addPhotoComponent({
        variables: {
          payload: {
            containerId,
            photoId: uploadedImageData,
            sortOrder: photoData?.sortOrder ?? 0,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: ['PageContainerBySlug'],
      }).then(() => {
        updateWebsitePreview();
      });
    }
  });

  return (
    <>
      <LayoutVirtualScrollableArea>
        <Flex
          paddingX={[6, isSingleComponent ? 0 : 5]}
          paddingTop={8}
          paddingBottom={pxToRem(132)}
        >
          <Flex
            paddingX={[0, 6]}
            width="100%"
            flexDirection="column"
          >
            <Flex
              flexDirection="column"
              gap={7}
            >
              {imageUrl && !isLoadingPhoto ? (
                <Flex
                  flexDirection={'column'}
                  justifyContent={'center'}
                  rowGap={6}
                >
                  <Flex
                    overflow={'hidden'}
                    borderRadius={3}
                  >
                    <img
                      src={imageUrl}
                      alt=""
                      width="100%"
                    />
                  </Flex>
                  <ButtonV2
                    variant="ghost"
                    intent="neutral"
                    size="lg"
                    startIcon={<Import />}
                    onClick={handleReplacePhoto}
                  >
                    {tq`Replace Photo`}
                  </ButtonV2>
                  {isEditMode && (
                    <ButtonV2
                      variant="ghost"
                      intent="destructive"
                      size="lg"
                      startIcon={<Trash />}
                      onClick={handleDeleteBlock}
                    >
                      {tq`Delete Photo Block`}
                    </ButtonV2>
                  )}
                </Flex>
              ) : (
                <Flex
                  alignItems={'center'}
                  justifyContent={'center'}
                  flexDirection={'column'}
                  backgroundColor={'mono1'}
                  padding={8}
                  width={'100%'}
                  borderRadius={3}
                  border={'2px dashed'}
                  borderColor={'mono5'}
                >
                  <PhotoUploadIcon size={'xl'} />
                  <TextV2
                    typographyVariant={'hed3'}
                    paddingY={3}
                  >
                    {tq`Add Your Photo`}
                  </TextV2>
                  <TextV2
                    typographyVariant={'label2'}
                    color={'mono10'}
                    paddingBottom={6}
                  >
                    {tq`We support jpeg and PNG images.`}
                  </TextV2>
                  <ButtonV2
                    shape="rounded"
                    intent="neutral"
                    onClick={handleAddPhoto}
                  >
                    {tq`Upload Photo`}
                  </ButtonV2>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </LayoutVirtualScrollableArea>
      <BottomActions
        onCancel={handleCancel}
        onSave={handleSave}
        isLoading={isLoading}
        isSingleComponent={isSingleComponent}
        isSaveDisabled={uploadedImageData.length === 0}
      />
    </>
  );
};
